import { PlanningUpdateOutcomeEnum, SimplifiedUserRole } from '@platform/client/schemas';
import { Modal, ModalCallToAction } from '@platform/components';
import { useState } from 'react';

import {
  ErrorConfirmationContent,
  SubmittingContent,
  SuccessConfirmationContent,
} from './ConfirmationContent';
import { SelectionContent } from './SelectionContent';
import { OtherUpdateContent } from './OtherUpdateContent';
import { ProjectHub } from '@platform/client';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'wouter';
import { UnresponsiveUpdateContent } from './UnresponsiveUpdateContent';
import { useForm, UseFormHandleSubmit, SubmitHandler } from 'react-hook-form';
import { useApplicationLocalStorage } from '../../application/application-hook';

export enum PlanUpdateModalStage {
  Update_Outcome = 'update-outcome',
  Success_Confirmed = 'success',
  Unresponsive = 'unresponsive',
  Unresponsive_Confirmed = 'unresponsive-confirmed',
  Different_Update = 'different-update',
  NonSuccess_Update_Confirmed = 'nonsuccess-update-confirmed',
  Submitting = 'submitting',
  Error = 'error',
}

export type PlanUpdateFormData = {
  projectId: number;
  outcome: PlanningUpdateOutcomeEnum;
  notes: string | null;
};

export const PlanUpdateModal = (props: {
  projectId: number;
  isOpen: boolean;
  onClose: () => void;
  userType: SimplifiedUserRole;
  isTeam: boolean;
}) => {
  const { assignmentDiscoveryId } = useParams<{ assignmentDiscoveryId: string }>();
  const [modalStage, setModalStage] = useState<PlanUpdateModalStage>(
    PlanUpdateModalStage.Update_Outcome,
  );

  const { handleSubmit, control, formState, register } = useForm<PlanUpdateFormData>({
    defaultValues: {
      projectId: props.projectId,
    },
  });
  // we could rename this now to form local storage hook?
  const { updateApplicationData, getApplicationDataByKey, clearApplicationData } =
    useApplicationLocalStorage<PlanUpdateFormData>();

  const progress = getProgress(modalStage);
  const closeModal = () => {
    setModalStage(PlanUpdateModalStage.Update_Outcome);
    props.onClose();
  };

  const queryClient = useQueryClient();
  const postPlanUpdateMutator = ProjectHub.usePostPlanUpdate({
    mutation: {
      onMutate: () => {
        setModalStage(PlanUpdateModalStage.Submitting);
      },
      onSuccess: () => {
        const selectedOutcome = getApplicationDataByKey('outcome');
        queryClient.invalidateQueries({
          queryKey: ProjectHub.getGetProjectHubDataQueryKey({
            assignment_discovery_id: parseInt(assignmentDiscoveryId),
          }),
        });
        queryClient.invalidateQueries({
          queryKey: ProjectHub.getGetTeamProjectHubDataQueryKey({
            project_id: props.projectId,
          }),
        });
        if (selectedOutcome === PlanningUpdateOutcomeEnum.complete) {
          setModalStage(PlanUpdateModalStage.Success_Confirmed);
        } else if (selectedOutcome === PlanningUpdateOutcomeEnum.unresponsive) {
          setModalStage(PlanUpdateModalStage.Unresponsive_Confirmed);
        } else {
          setModalStage(PlanUpdateModalStage.NonSuccess_Update_Confirmed);
        }
        clearApplicationData();
      },
      onError: () => {
        setModalStage(PlanUpdateModalStage.Error);
      },
    },
  });

  const onSubmit: SubmitHandler<PlanUpdateFormData> = (data) => {
    postPlanUpdateMutator.mutate({
      data: {
        projectId: props.projectId,
        outcome: getApplicationDataByKey('outcome'),
        notes: data.notes ?? null,
        isTeam: props.isTeam,
      },
    });
  };
  const ctaProps = getCTAProps(
    getApplicationDataByKey,
    onSubmit,
    handleSubmit,
    modalStage,
    setModalStage,
    closeModal,
  );

  const updateSelectionItems = [
    {
      value: PlanningUpdateOutcomeEnum.complete,
      title: 'Yes',
    },
    {
      value: PlanningUpdateOutcomeEnum.unresponsive,
      title: `No, the ${props.userType == SimplifiedUserRole.PROFESSIONAL ? 'representative' : 'professional'} is unresponsive`,
    },
    {
      value: PlanningUpdateOutcomeEnum.in_progress,
      title: `No, but we're working on it`,
    },
    {
      value: PlanningUpdateOutcomeEnum.need_help,
      title: 'I need more help',
    },
  ];

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={closeModal}
      title='Planning update'
      headerDivision='step'
      progress={progress}
      variant='full-screen'
      primaryCTA={ctaProps.primaryCTA}
      secondaryCTA={ctaProps.secondaryCTA}
    >
      {modalStage === PlanUpdateModalStage.Update_Outcome && (
        <SelectionContent
          register={() => register('outcome')} // intentionally do not use the RHF required rule to allow for submission on second step
          description='Have you scoped the project and decided on deliverables and timeline?'
          items={updateSelectionItems}
          onChange={(value) => {
            updateApplicationData({ outcome: value as PlanningUpdateOutcomeEnum });
          }}
          defaultValue={getApplicationDataByKey('outcome')}
        />
      )}
      {modalStage === PlanUpdateModalStage.Success_Confirmed && (
        <SuccessConfirmationContent>
          Nice job finalizing your plan! You're ready to get started. We'll guide you through the
          next step.
        </SuccessConfirmationContent>
      )}
      {modalStage === PlanUpdateModalStage.NonSuccess_Update_Confirmed && (
        <SuccessConfirmationContent>
          Thanks for sharing your update. It helps us better support your project and track your
          progress.
        </SuccessConfirmationContent>
      )}
      {modalStage === PlanUpdateModalStage.Unresponsive_Confirmed && (
        <SuccessConfirmationContent>
          Thanks for the update! We'll follow up with you by email soon.
        </SuccessConfirmationContent>
      )}
      {modalStage === PlanUpdateModalStage.Unresponsive && (
        <UnresponsiveUpdateContent control={control} name='notes' />
      )}
      {modalStage === PlanUpdateModalStage.Different_Update && (
        <OtherUpdateContent control={control} formState={formState} name='notes' />
      )}
      {modalStage === PlanUpdateModalStage.Submitting && <SubmittingContent />}
      {modalStage === PlanUpdateModalStage.Error && <ErrorConfirmationContent />}
    </Modal>
  );
};

const getCTAProps = (
  getApplicationData: (key: string) => PlanningUpdateOutcomeEnum | undefined,
  onSubmit: SubmitHandler<PlanUpdateFormData>,
  handleSubmit: UseFormHandleSubmit<PlanUpdateFormData>,
  modalStage: PlanUpdateModalStage,
  setModalStage: (modalStage: PlanUpdateModalStage) => void,
  closeModal: () => void,
): { primaryCTA: ModalCallToAction; secondaryCTA?: ModalCallToAction } => {
  switch (modalStage) {
    case PlanUpdateModalStage.Update_Outcome:
      return {
        primaryCTA: {
          text: 'Save & Continue',
          callback: () => {
            const outcomeSelection = getApplicationData('outcome');
            if (outcomeSelection === PlanningUpdateOutcomeEnum.complete) {
              handleSubmit(onSubmit)();
            } else if (outcomeSelection === PlanningUpdateOutcomeEnum.unresponsive) {
              setModalStage(PlanUpdateModalStage.Unresponsive);
            } else if (
              outcomeSelection === PlanningUpdateOutcomeEnum.in_progress ||
              outcomeSelection === PlanningUpdateOutcomeEnum.need_help
            ) {
              setModalStage(PlanUpdateModalStage.Different_Update);
            }
          },
        },
        secondaryCTA: {
          text: 'Back',
          callback: () => {
            closeModal();
          },
        },
      };
    case PlanUpdateModalStage.Unresponsive:
    case PlanUpdateModalStage.Different_Update:
      return {
        primaryCTA: {
          text: 'Save & Continue',
          callback: () => {
            handleSubmit(onSubmit)();
          },
        },
        secondaryCTA: {
          text: 'Go back',
          callback: () => {
            setModalStage(PlanUpdateModalStage.Update_Outcome);
          },
        },
      };
    default:
      return {
        primaryCTA: {
          text: 'Close',
          callback: () => {
            closeModal();
          },
        },
      };
  }
};
const getProgress = (modalStage: PlanUpdateModalStage): number => {
  if (
    modalStage === PlanUpdateModalStage.Success_Confirmed ||
    modalStage === PlanUpdateModalStage.NonSuccess_Update_Confirmed
  ) {
    return 100;
  } else if (modalStage === PlanUpdateModalStage.Update_Outcome) {
    return 15;
  } else {
    return 66;
  }
};
